/**
 * Admin › Events › ICS Feeds — register public iCal feeds for auto-discovery.
 * Compact management panel that lives inline on the Events admin page.
 */
import { useEffect, useState, useCallback } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Play, RefreshCw, TestTube, Rss, ExternalLink } from "lucide-react";

const CATEGORIES = [
  "Food & Agriculture", "Environmental", "Health & Wellness",
  "Community Service", "Education", "Arts & Culture",
  "Heritage & Memorial", "Government & Civic", "Animal Welfare", "Youth",
];

const EMPTY_FORM = {
  url: "", name: "", category: "Community Service",
  country: "", state: "", city: "",
  auto_approve: false, active: true, notes: "",
};

export default function AdminEventFeeds() {
  const [feeds, setFeeds] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/admin/event-feeds");
      setFeeds(data.feeds || []);
      setStatus(data.status || null);
    } catch (e) {
      toast.error("Failed to load feeds");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const test = async () => {
    if (!form.url) return toast.error("Enter a feed URL first");
    setBusy(true); setPreview(null);
    try {
      const { data } = await http.post("/admin/event-feeds/test", form);
      setPreview(data);
      toast.success(`Feed OK — ${data.total_events_in_feed} events found`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Feed test failed");
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.url) return toast.error("Feed URL is required");
    setBusy(true);
    try {
      await http.post("/admin/event-feeds", form);
      toast.success("Feed saved");
      setForm(EMPTY_FORM); setShowForm(false); setPreview(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this feed? Events already imported stay in place.")) return;
    try {
      await http.delete(`/admin/event-feeds/${id}`);
      toast.success("Feed deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const ingestOne = async (id) => {
    try {
      const { data } = await http.post(`/admin/event-feeds/${id}/ingest`);
      toast.success(`+${data.inserted || 0} new · ↺${data.updated || 0} · skip ${data.skipped || 0}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ingest failed");
    }
  };

  const ingestAll = async () => {
    try {
      await http.post(`/admin/event-feeds/ingest-all`);
      toast.success("Ingestion queued for every active feed");
    } catch { toast.error("Ingest-all failed"); }
  };

  return (
    <div data-testid="admin-event-feeds" className="mt-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">EVENTS · AUTO-DISCOVERY</div>
          <h3 className="font-display text-2xl">iCal / ICS feed sources.</h3>
          <p className="text-xs opacity-70 mt-1 max-w-2xl">
            Register a public .ics feed (city calendars, library systems, universities, parks
            departments, community centers). Feeds refresh every 6 hours automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={ingestAll} data-testid="feed-ingest-all" className="wofy-btn-ghost text-sm">
            <Play className="w-4 h-4" /> Ingest all
          </button>
          <button onClick={load} className="wofy-btn-ghost text-sm" aria-label="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowForm((s) => !s)}
            data-testid="feed-add-toggle"
            className="wofy-btn-primary text-sm"
          >
            <Plus className="w-4 h-4" /> {showForm ? "Close" : "Add feed"}
          </button>
        </div>
      </div>

      {/* Status card */}
      {status && (
        <div className="mt-4 grid gap-3 md:grid-cols-4 text-sm">
          <StatCard label="ACTIVE FEEDS" value={status.feeds_active ?? 0} sub={`${status.feeds_total ?? 0} total`} />
          <StatCard label="EVENTS FROM ICS" value={status.events_from_ics ?? 0} sub="all-time" />
          <StatCard label="PENDING" value={status.events_pending ?? 0} sub="need approval" />
          <StatCard label="AI SUMMARIES" value={"—"} sub="auto-generated on approve" />
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <form onSubmit={submit} data-testid="feed-form" className="mt-6 wofy-card space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="block">
              <span className="text-xs font-mono uppercase opacity-70">Feed URL (required)</span>
              <input
                data-testid="feed-url" required type="url"
                value={form.url} onChange={(e) => upd("url", e.target.value)}
                placeholder="https://…/events.ics"
                className="wofy-input mt-1"
              />
            </label>
            <label className="block">
              <span className="text-xs font-mono uppercase opacity-70">Display name</span>
              <input
                data-testid="feed-name"
                value={form.name} onChange={(e) => upd("name", e.target.value)}
                placeholder="City of Austin — Parks Events"
                className="wofy-input mt-1"
              />
            </label>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <label className="block">
              <span className="text-xs font-mono uppercase opacity-70">Category</span>
              <select
                value={form.category} onChange={(e) => upd("category", e.target.value)}
                className="wofy-input mt-1"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-mono uppercase opacity-70">Country</span>
              <input value={form.country} onChange={(e) => upd("country", e.target.value)} className="wofy-input mt-1" />
            </label>
            <label className="block">
              <span className="text-xs font-mono uppercase opacity-70">City</span>
              <input value={form.city} onChange={(e) => upd("city", e.target.value)} className="wofy-input mt-1" />
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.auto_approve} onChange={(e) => upd("auto_approve", e.target.checked)} />
              Auto-approve events from this feed (trusted source)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => upd("active", e.target.checked)} />
              Active
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button" onClick={test} disabled={busy}
              data-testid="feed-test"
              className="wofy-btn-ghost text-sm"
            >
              <TestTube className="w-4 h-4" /> Test feed
            </button>
            <button type="submit" disabled={busy} data-testid="feed-save" className="wofy-btn-primary text-sm">
              Save feed
            </button>
          </div>
          {preview && (
            <div className="mt-2 text-xs opacity-80 border-t pt-3">
              <div className="font-mono uppercase mb-1">PREVIEW · {preview.total_events_in_feed} events</div>
              <ul className="grid md:grid-cols-2 gap-1">
                {(preview.sample || []).map((s, i) => (
                  <li key={i} className="truncate">
                    • <strong>{s.title}</strong> {s.starts ? `— ${s.starts}` : ""} {s.location ? `@ ${s.location}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </form>
      )}

      {/* Feed list */}
      {loading ? (
        <div className="mt-6 text-sm opacity-60">Loading feeds…</div>
      ) : feeds.length === 0 ? (
        <div className="mt-6 wofy-card text-center py-10">
          <Rss className="w-6 h-6 mx-auto opacity-40 mb-2" />
          <div className="font-display text-lg">No feeds registered yet.</div>
          <div className="text-xs opacity-60 mt-1">
            Try a public calendar. Example: US National Holidays via Google Calendar.
          </div>
        </div>
      ) : (
        <div className="mt-6 grid gap-3">
          {feeds.map((f) => (
            <div key={f.id} data-testid={`feed-row-${f.id}`} className="wofy-card flex flex-wrap items-start gap-4">
              <div className="flex-1 min-w-[280px]">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${f.active ? "bg-emerald-100 text-emerald-800" : "bg-neutral-200 text-neutral-600"}`}>
                    {f.active ? "ACTIVE" : "PAUSED"}
                  </span>
                  {f.auto_approve && (
                    <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                      AUTO-APPROVE
                    </span>
                  )}
                  <span className="text-[10px] font-mono opacity-60">{f.category}</span>
                </div>
                <div className="font-display text-lg">{f.name || "(Unnamed feed)"}</div>
                <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-xs opacity-70 break-all inline-flex items-center gap-1 hover:underline">
                  {f.url} <ExternalLink className="w-3 h-3" />
                </a>
                <div className="text-[11px] opacity-60 mt-1 font-mono">
                  {[f.city, f.state, f.country].filter(Boolean).join(" · ") || "no location"}
                  {f.last_ingested_at ? ` · last ingested ${f.last_ingested_at.slice(0, 16).replace("T", " ")}` : " · never ingested"}
                </div>
                {f.last_result && (
                  <div className="text-[11px] mt-1 font-mono opacity-70">
                    last run: +{f.last_result.inserted} new · ↺{f.last_result.updated} · skip {f.last_result.skipped}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => ingestOne(f.id)} className="wofy-btn-ghost text-xs">
                  <Play className="w-4 h-4" /> Ingest now
                </button>
                <button onClick={() => del(f.id)} className="wofy-btn-ghost text-xs" style={{ color: "#B23A3A" }} aria-label="Delete feed">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="wofy-card">
      <div className="overline">{label}</div>
      <div className="font-display text-2xl mt-1">{value}</div>
      {sub && <div className="text-xs opacity-60 mt-1">{sub}</div>}
    </div>
  );
}
